import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, unref as _unref, vModelText as _vModelText } from "vue"

const _hoisted_1 = { class: "field" }
const _hoisted_2 = {
  key: 0,
  class: "field is-horizontal"
}
const _hoisted_3 = { class: "field-label" }
const _hoisted_4 = { class: "label" }
const _hoisted_5 = { class: "field-body" }
const _hoisted_6 = { class: "control" }
const _hoisted_7 = { class: "select is-fullwidth" }
const _hoisted_8 = ["value"]
const _hoisted_9 = { class: "field is-horizontal" }
const _hoisted_10 = { class: "field-label" }
const _hoisted_11 = { class: "label" }
const _hoisted_12 = { class: "field-body" }
const _hoisted_13 = { class: "field" }
const _hoisted_14 = { class: "control" }
const _hoisted_15 = {
  key: 0,
  class: "help is-danger"
}
const _hoisted_16 = { class: "field" }
const _hoisted_17 = { class: "control" }
const _hoisted_18 = {
  key: 0,
  class: "help is-danger"
}
const _hoisted_19 = {
  key: 1,
  class: "help is-danger"
}

import { numeric, minValue } from '@vuelidate/validators';
import { ref, watch } from 'vue';
import useVuelidate from '@vuelidate/core';


export default /*@__PURE__*/_defineComponent({
  __name: 'InputPatternEditor',
  props: {
  value: {
    type: String,
    default: '.',
  },
  showType: {
    type: Boolean,
    default: true,
  },
},
  emits: ["updated"],
  setup(__props, { emit: __emit }) {

const AVAILABLE_PATTERN_TYPES: { [key: string]: string } = {
  Any: '.',
  Alphanumeric: '[a-zA-Z0-9]',
  'Letters Only': '[a-zA-Z]',
  'Numbers Only': '[0-9]',
  Decimal: '(\\d+(\\.\\d{0,2})?|\\.?\\d{1,2})',
};

const greaterThan = (value: string, vm: any) => {
  if (vm.maxLength.value === '' || vm.minLength.value === '') {
    return true; // No value for either min or max, its valid
  }
  return parseInt(value, 10) >= parseInt(vm.minLength.value, 10);
};

const props = __props;

const emit = __emit;

const selectedType = ref('');
const minLength = ref('');
const maxLength = ref('');

const validations = {
  minLength: {
    numeric,
    minValue: minValue(0),
  },
  maxLength: {
    numeric,
    minValue: minValue(0),
    greaterThan,
  },
};

const $v = useVuelidate(validations, { minLength, maxLength });

watch(
  () => props.value,
  () => {
    parsePattern();

    $v.value.$touch();
    patternChanged();
  },
  { immediate: true },
);

function parsePattern() {
  if (!props.value || props.value.length === 0) {
    selectedType.value = 'Any';
    return;
  }
  selectedType.value = getSelectedType() || 'Any';

  if (canBeAnyLength()) {
    minLength.value = '';
    maxLength.value = '';
  } else if (props.value.indexOf('{') > -1) {
    const openingBrace = props.value.indexOf('{') + 1;
    const closingBrace = props.value.indexOf('}');
    const lengths = props.value.substring(openingBrace, closingBrace).split(',');

    minLength.value = lengths[0];
    maxLength.value = lengths[1];
  }
}

function buildPattern() {
  let pattern: string = `${AVAILABLE_PATTERN_TYPES[selectedType.value]}`;
  if (selectedType.value === 'Decimal') {
    pattern = buildDecimalPattern(pattern);
  } else {
    if (minLength.value.length === 0 && maxLength.value.length === 0) {
      pattern += '+';
    } else {
      if (minLength.value.length === 0) {
        pattern += `{1,${maxLength.value}}`;
      } else {
        pattern += `{${minLength.value},${maxLength.value}}`;
      }
    }
  }
  pattern = `^${pattern}$`;

  return pattern;
}

function patternChanged() {
  const newPattern = buildPattern();
  emit('updated', newPattern, $v.value.$invalid);
}

function getSelectedType() {
  const keys = Object.keys(AVAILABLE_PATTERN_TYPES);
  keys.shift();
  return keys.find((key) => props.value.indexOf(AVAILABLE_PATTERN_TYPES[key]) > -1);
}

function canBeAnyLength() {
  if (isRegexEndedWithPlusSymbol()) {
    return true;
  } else if (selectedType.value === 'Decimal') {
    return canDecimalBeAnyLength();
  }
  return false;
}

function isRegexEndedWithPlusSymbol() {
  const regExWithoutStartEndLineCharacters = stripLineStartAndEndCharacters(props.value);
  return regExWithoutStartEndLineCharacters.endsWith('+');
}

function canDecimalBeAnyLength() {
  const regExWithoutStartEndLineCharacters = stripLineStartAndEndCharacters(props.value);
  return isRegexDefaultDecimalRegex(regExWithoutStartEndLineCharacters);
}

function isRegexDefaultDecimalRegex(regex: string) {
  return AVAILABLE_PATTERN_TYPES.Decimal === regex;
}

function stripLineStartAndEndCharacters(input: string) {
  if (input.startsWith('^') && input.endsWith('$')) {
    return input.substring(1, input.length - 1);
  }
  return input;
}

function buildDecimalPattern(pattern: string) {
  if (minLength.value.length || maxLength.value.length) {
    if (minLength.value.length === 0) {
      pattern = `(?=^.{1,${maxLength.value}}$)${pattern}`;
    } else {
      pattern = `(?=^.{${minLength.value},${maxLength.value}}$)${pattern}`;
    }
  }
  return pattern;
}

return (_ctx: any,_cache: any) => {
  const _component_n_tooltip = _resolveComponent("n-tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (__props.showType)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("label", _hoisted_4, [
              _cache[3] || (_cache[3] = _createTextVNode(" Type ", -1)),
              _createVNode(_component_n_tooltip, {
                position: "top",
                text: "The type of characters that will be accepted as valid input"
              })
            ])
          ]),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("div", _hoisted_7, [
                _withDirectives(_createElementVNode("select", {
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedType).value = $event)),
                  onChange: patternChanged
                }, [
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(AVAILABLE_PATTERN_TYPES, (patternValue, patternLabel) => {
                    return _createElementVNode("option", {
                      key: patternLabel,
                      value: patternLabel
                    }, _toDisplayString(patternLabel), 9, _hoisted_8)
                  }), 64))
                ], 544), [
                  [_vModelSelect, selectedType.value]
                ])
              ])
            ])
          ])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_9, [
      _createElementVNode("div", _hoisted_10, [
        _createElementVNode("label", _hoisted_11, [
          _cache[4] || (_cache[4] = _createTextVNode(" Min / Max Length ", -1)),
          _createVNode(_component_n_tooltip, {
            position: "top",
            text: "Minimum and Maximum allowed character limits. If left blank, there is no limit"
          })
        ])
      ]),
      _createElementVNode("div", _hoisted_12, [
        _createElementVNode("div", _hoisted_13, [
          _createElementVNode("div", _hoisted_14, [
            _withDirectives(_createElementVNode("input", {
              class: "input",
              type: "text",
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref($v).minLength.$model) = $event)),
              onChange: patternChanged
            }, null, 544), [
              [
                _vModelText,
                _unref($v).minLength.$model,
                void 0,
                { trim: true }
              ]
            ]),
            (_unref($v).minLength.$error && !_unref($v).minLength.numeric)
              ? (_openBlock(), _createElementBlock("p", _hoisted_15, " Min length must be a number "))
              : _createCommentVNode("", true)
          ])
        ]),
        _createElementVNode("div", _hoisted_16, [
          _createElementVNode("div", _hoisted_17, [
            _withDirectives(_createElementVNode("input", {
              class: "input",
              type: "text",
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref($v).maxLength.$model) = $event)),
              onChange: patternChanged
            }, null, 544), [
              [
                _vModelText,
                _unref($v).maxLength.$model,
                void 0,
                { trim: true }
              ]
            ]),
            (_unref($v).maxLength.$error && !_unref($v).maxLength.numeric)
              ? (_openBlock(), _createElementBlock("p", _hoisted_18, " Max length must be a number "))
              : _createCommentVNode("", true),
            (_unref($v).maxLength.$error && !_unref($v).maxLength.greaterThan)
              ? (_openBlock(), _createElementBlock("p", _hoisted_19, " Max length must be greater than the Min length "))
              : _createCommentVNode("", true)
          ])
        ])
      ])
    ])
  ]))
}
}

})